import { DownloadOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Space, Table, Tag, Upload, message } from 'antd';
import { useEffect, useState } from 'react';
import type { CurrentUser } from '@cell-bank/shared';
import { useTranslation } from 'react-i18next';

type QcResult = {
  id: string;
  sampleId: string;
  templateCode: string | null;
  testedAt: string;
  conclusion: string;
  attachments: { id: string; originalFilename: string }[];
};

export function QualityPage() {
  const { t } = useTranslation();
  const [results, setResults] = useState<QcResult[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser>();
  const [templatesEnabled, setTemplatesEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messageApi, holder] = message.useMessage();
  const canUpload = currentUser?.permissions.includes('attachment:manage') ?? false;

  const load = async () => {
    setLoading(true);
    try {
      const [userResponse, resultResponse, settingsResponse] = await Promise.all([
        fetch('/api/v1/auth/me', { credentials: 'include' }),
        fetch('/api/v1/qc-result-sets', { credentials: 'include' }),
        fetch('/api/v1/qc-settings', { credentials: 'include' }),
      ]);
      if (!userResponse.ok || !resultResponse.ok || !settingsResponse.ok) throw new Error();
      setCurrentUser((await userResponse.json()) as CurrentUser);
      setResults(((await resultResponse.json()) as { items: QcResult[] }).items);
      setTemplatesEnabled(((await settingsResponse.json()) as { templatesEnabled: boolean }).templatesEnabled);
    } catch {
      await messageApi.error(t('quality.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const uploadAttachment = async (resultId: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`/api/v1/qc-result-sets/${resultId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body,
    });
    if (!response.ok) {
      await messageApi.error(t('quality.uploadFailed'));
      return;
    }
    await load();
    await messageApi.success(t('quality.uploaded'));
  };

  return (
    <section>
      {holder}
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>
          {t('quality.refresh')}
        </Button>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
        dataSource={results}
        pagination={{ defaultPageSize: 50 }}
        columns={[
          ...(templatesEnabled ? [{ title: t('quality.template'), dataIndex: 'templateCode' }] : []),
          { title: t('quality.sampleId'), dataIndex: 'sampleId' },
          { title: t('quality.testedAt'), dataIndex: 'testedAt' },
          {
            title: t('quality.conclusion'),
            dataIndex: 'conclusion',
            render: (value: string) => <Tag color={value === 'PASSED' ? 'green' : value === 'FAILED' ? 'red' : 'gold'}>{value}</Tag>,
          },
          {
            title: t('quality.attachments'),
            render: (_, row: QcResult) => (
              <Space wrap>
                {row.attachments.map((item) => (
                  <Button key={item.id} type="link" icon={<DownloadOutlined />} href={`/api/v1/attachments/${item.id}/download`}>
                    {item.originalFilename}
                  </Button>
                ))}
                {canUpload && (
                  <Upload
                    showUploadList={false}
                    beforeUpload={(file) => {
                      void uploadAttachment(row.id, file);
                      return false;
                    }}
                    accept=".pdf,.png,.jpg,.jpeg,.csv,.txt"
                  >
                    <Button type="link" icon={<UploadOutlined />}>{t('quality.upload')}</Button>
                  </Upload>
                )}
                {!row.attachments.length && !canUpload ? '-' : null}
              </Space>
            ),
          },
        ]}
      />
    </section>
  );
}
